import { Button, Grid, InputAdornment, makeStyles, TextField, Tooltip, Typography } from '@material-ui/core';
import { InfoOutlined, Save } from '@material-ui/icons';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import SourceConfiguration from '../components/SourceConfiguration';
import useApi from '../hooks/useApi';
import { useCustomerMode } from '../hooks/useCustomerMode';
import useCustomSnackbarGlobal from '../hooks/useSnackbarGlobal';
import { useWizard } from '../hooks/useWizard';
import ApiService from '../modules/api-service';
import { CostsDTO, WizardStep } from '../modules/generated/api';

const useStyles = makeStyles((theme) => ({
  headline: {
    marginBottom: theme.spacing(2),
  },
  sectionHeadline: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    textDecorationLine: 'none',
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
}));

type ConfigurationCostsFormProps = {
  showInWizard?: boolean;
};

const ConfigurationCostsForm = ({ showInWizard }: ConfigurationCostsFormProps) => {
  const classes = useStyles();
  const { fetch, data: configurations } = useApi<CostsDTO>();
  const methods = useForm();
  const snackbar = useCustomSnackbarGlobal();
  const { useTrackWizardConfirmationOnPageView } = useWizard();
  const { customerModeAvailable } = useCustomerMode();

  const {
    reset,
    register,
    formState: { errors },
  } = methods;
  const { t } = useTranslation();
  const showCustomerModeCosts = !showInWizard && customerModeAvailable;
  // While the section is hidden (`register` still runs on every render, and RHF only submits registered fields)
  // the loaded margin is still sent back unchanged, but not validated:
  // an invalid value the user cannot see would otherwise block saving the whole form.
  const { ref: customerMarginPercentRef, ...customerMarginPercentField } = register('customerMarginPercent', {
    // An empty field means no margin. It must be sent as 0: the BE ignores null and would keep the stored margin.
    setValueAs: (value: string | number) => (value === '' ? 0 : Number(value)),
    min: showCustomerModeCosts ? 0 : undefined,
    // At most 2 decimals
    validate: (value: number) => !showCustomerModeCosts || Number.isInteger(Number((value * 100).toFixed(8))),
  });

  useEffect(() => {
    fetch(ApiService.dealer.dealerControllerGetCosts());
  }, [fetch]);

  useEffect(() => {
    if (configurations) {
      reset(configurations);
    }
  }, [configurations, reset]);

  const handleOnSubmit = async (data: { [x: string]: any }) => {
    const result = await fetch(ApiService.dealer.dealerControllerSaveCosts(data));
    if (result && result.data) {
      snackbar.showSuccess(t('alerts.successSaved'));
    } else {
      snackbar.showError(t('alerts.errorRaised'));
    }
  };

  useTrackWizardConfirmationOnPageView(WizardStep.Costs, showInWizard);

  return (
    <FormProvider {...methods}>
      <form id="costs-form" onSubmit={methods.handleSubmit(handleOnSubmit)}>
        <Typography className={classes.headline} variant="h1">
          {t('configuration.costConfig')}
        </Typography>
        <Grid alignItems="center" container spacing={4}>
          <SourceConfiguration />
        </Grid>

        {showCustomerModeCosts && (
          <>
            <Typography className={classes.sectionHeadline} variant="h2">
              {t('configuration.customerModeCosts')}
            </Typography>
            <Grid alignItems="center" container spacing={4}>
              <Grid item xs={4}>
                <TextField
                  inputRef={customerMarginPercentRef}
                  {...customerMarginPercentField}
                  label={t('configuration.customerMargin')}
                  type="number"
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Tooltip title={t('configuration.customerMarginHint') as string}>
                          <InfoOutlined fontSize="small" />
                        </Tooltip>
                      </InputAdornment>
                    ),
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  error={!!errors.customerMarginPercent}
                />
              </Grid>
            </Grid>
          </>
        )}

        <Button className={classes.saveButton} color="primary" endIcon={<Save />} type="submit" variant="contained">
          {t('common.save')}
        </Button>
      </form>
    </FormProvider>
  );
};
export default ConfigurationCostsForm;
